import { SBAsyncSelect } from '@supplement-bacon/react-async-select'
import { App, Card, Form, Space, Typography } from 'antd'
import { FC, useEffect, useState } from 'react'
import { DEFAULT_ERROR, DEFAULT_SUCCESS } from '../../App'
import { LoyaltyTier } from '../../models/LoyaltyTier'
import { updateLoyaltyTiersReferralRule } from '../../services/LoyaltyTiersService'
import { Entity } from '../../utils/Entities'
import { Permission } from '../../utils/permissions'
import { useHasAccess } from '../HasAccess'
import { LoyaltyTierTag } from './Tag'

const { Title } = Typography

interface Props {
  loyaltyTier: LoyaltyTier
  giveLoyaltyTier?: LoyaltyTier
}

const ReferralCard: FC<Props> = ({
  loyaltyTier,
  giveLoyaltyTier: giveLoyaltyTierProps,
}) => {
  const [form] = Form.useForm()
  const { message } = App.useApp()
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [isFirstRender, setIsFirstRender] = useState<boolean>(true)
  const giveLoyaltyTier = Form.useWatch('giveLoyaltyTier', form)
  const { hasAccess } = useHasAccess()

  useEffect(() => {
    if (giveLoyaltyTierProps) {
      form.setFieldValue('giveLoyaltyTier', giveLoyaltyTierProps.id)
    }
  }, [giveLoyaltyTierProps])

  /**
   * `giveLoyaltyTier` might be `undefined` in case the select was cleared
   * However on component initialization `giveLoyaltyTier` will be also undefined
   * Therefore we use isFirstRender to avoid trigerring a request on first component render
   *
   * On subsequent renders, we make sure `giveLoyaltyTier` is different from `giveLoyaltyTierProps`
   * As when `form.setFieldValue()` is triggered in the above useEffect it will trigger a change
   * And if values are equals, there is no need to trigger a request
   */
  useEffect(() => {
    if (isFirstRender) {
      setIsFirstRender(false)
      return
    }
    if (giveLoyaltyTierProps && giveLoyaltyTierProps.id === giveLoyaltyTier) {
      return
    }
    setIsLoading(true)
    updateLoyaltyTiersReferralRule(loyaltyTier.id, giveLoyaltyTier)
      .then(() => {
        message.success(DEFAULT_SUCCESS)
      })
      .catch(() => {
        message.error(DEFAULT_ERROR)
      })
      .finally(() => setIsLoading(false))
  }, [giveLoyaltyTier])

  return (
    <Card>
      <Space size={0}>
        <Title className="m-0" level={4}>
          When a <LoyaltyTierTag model={loyaltyTier} size="large" />
          user refers a new user, his child inherits a
        </Title>
        <Form form={form} style={{ paddingInline: 5 }} disabled={isLoading}>
          <Form.Item
            name="giveLoyaltyTier"
            style={{ minWidth: 160, margin: 0 }}
          >
            <SBAsyncSelect
              allowClear
              entity={Entity.loyaltyTiers}
              disabled={!hasAccess(Permission.updateReferral)}
            />
          </Form.Item>
        </Form>
        <Title className="m-0" level={4}>
          loyalty tier.
        </Title>
      </Space>
    </Card>
  )
}

type ReferralCardType = { ReferralCard: typeof ReferralCard }

export { ReferralCard, type ReferralCardType }
